import React, { useState } from "react";
import { Check, Star, ArrowRight, Quote } from "lucide-react";
import { communityPerks, membershipTiers, testimonials } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { createMembership } from "../lib/api";

export default function Community() {
  const { toast } = useToast();
  const [joined, setJoined] = useState(null);

  const join = async (tier) => {
    try {
      await createMembership({ tier });
      setJoined(tier);
      toast({ title: `Welcome to ${tier}!`, description: "Check your inbox to finish setting up your account." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-navy text-cream">
        <div className="max-w-5xl mx-auto px-5 py-20 text-center">
          <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">The BOSSY Community</p>
          <h1 className="font-serif-display text-5xl md:text-7xl font-bold mt-4">You don't have to do this alone.</h1>
          <p className="font-ui text-white/80 mt-6 text-lg leading-relaxed max-w-2xl mx-auto">
            A private space where women business owners share the real stuff — truths, questions, wins, and worries —
            inside a supportive, members-only forum with over 10 years of content to search from.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-5xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-12">What's inside</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {communityPerks.map((perk) => (
            <div key={perk} className="flex items-start gap-4 bg-white rounded-xl p-5 border" style={{ borderColor: "var(--cream-dark)" }}>
              <div className="w-9 h-9 rounded-full bg-teal/15 flex items-center justify-center shrink-0">
                <Check size={18} className="text-teal" />
              </div>
              <p className="font-ui text-navy/80 leading-relaxed">{perk}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Membership tiers */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-navy text-center">Choose your seat at the table</h2>
          <p className="font-ui text-navy/60 text-center mt-3">Cancel anytime. Real people, real support.</p>
          <div className="grid md:grid-cols-3 gap-7 mt-14">
            {membershipTiers.map((t) => (
              <div key={t.name} className={`rounded-2xl p-8 flex flex-col relative ${t.highlight ? "bg-navy text-cream shadow-2xl md:-translate-y-4" : "bg-cream border"}`} style={!t.highlight ? { borderColor: "var(--cream-dark)" } : {}}>
                {t.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal text-navy px-4 py-1 rounded-full font-ui text-xs font-bold flex items-center gap-1">
                    <Star size={13} fill="currentColor" /> MOST POPULAR
                  </span>
                )}
                <h3 className={`font-serif-display text-2xl font-bold ${t.highlight ? "text-cream" : "text-navy"}`}>{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`font-serif-display text-5xl font-bold ${t.highlight ? "text-cream" : "text-navy"}`}>{t.price}</span>
                  <span className={`font-ui ${t.highlight ? "text-white/60" : "text-navy/50"}`}>{t.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check size={18} className={t.highlight ? "text-teal" : "text-teal"} />
                      <span className={`font-ui text-sm ${t.highlight ? "text-white/85" : "text-navy/80"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => join(t.name)} className={`mt-7 py-3.5 rounded-full font-ui font-semibold transition-colors flex items-center justify-center gap-2 ${t.highlight ? "bg-teal text-navy hover:bg-cream" : "bg-navy text-cream hover:bg-orange"}`}>
                  {joined === t.name ? <><Check size={17} /> Joined</> : <>Join {t.name} <ArrowRight size={16} /></>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-12">From our members</h2>
        <div className="grid md:grid-cols-3 gap-7">
          {testimonials.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl p-7 border" style={{ borderColor: "var(--cream-dark)" }}>
              <Quote size={30} className="text-teal/40" />
              <p className="font-serif-body text-xl text-navy mt-4 leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-5 font-ui">
                <p className="font-semibold text-navy">{t.name}</p>
                <p className="text-sm text-navy/55">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
